import { useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'contexts/ThemeContext';
import {
  HardDrive, RefreshCcw, Pause, Play, Gauge, List, CheckCircle2,
  AlertTriangle, XCircle, Clock, Loader2, Trash2, Database, Hourglass,
  RotateCcw, Filter, Eye, Inbox, X, FileText, Menu
} from 'lucide-react';
import { useSidebar } from 'contexts/SidebarContext';
import {
  getQueueList,
  getQueueStatus,
  getQueueJobs,
  clearRedisQueue,
  pauseQueue,
  resumeQueue,
  retryFailedJobs,
  drainQueue,
  removeQueueJob,
  bulkRemoveQueueJobs,
  QueueListResponse,
  AllQueuesStatusResponse,
  QueueJob,
  QueueJobsQueryParams,
  ClearQueueParams
} from 'services/admin/superAdminService';
import Spinner from 'components/common/Spinner';
import { logger } from 'utils/logger';
import { getErrorMessage } from 'utils/errors';

type QueueJobStatus = 'waiting' | 'active' | 'completed' | 'failed' | 'delayed';

export default function QueueManagement() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { toggleSidebar } = useSidebar();

  // Color tokens (updated to match unified card container pattern)
  const containerBg = isDark ? '#1D293D' : '#F8FAFC';
  const mainBg = isDark ? '#0F172B' : '#FFFFFF';
  const mainShadow = isDark ? '0px 1px 2px rgba(2, 6, 24, 0.5)' : '0px 1px 2px rgba(43, 127, 255, 0.1)';
  const headerBorder = isDark ? '#1D293D' : '#F1F5F9';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const subtitleColor = isDark ? '#90A1B9' : '#62748E';
  const tableHeaderBg = isDark ? '#1D293D' : '#F8FAFC';
  const tableBorderColor = isDark ? '#1E293B' : '#E2E8F0';
  const inputBg = isDark ? '#1D293D' : '#FFFFFF';
  const inputBorder = isDark ? '#314158' : '#D1D5DB';
  const inputText = isDark ? '#E2E8F0' : '#1E293B';
  const buttonBg = isDark ? '#1D293D' : '#F3F4F6';
  const buttonText = isDark ? '#90A1B9' : '#374151';
  const paginationBg = isDark ? '#1D293D' : '#FFFFFF';
  const paginationBorder = isDark ? '#314158' : '#D1D5DB';
  const paginationText = isDark ? '#90A1B9' : '#374151';
  const successBg = isDark ? '#0D2818' : '#F0FDF4';
  const successBorder = isDark ? '#1A4028' : '#BBF7D0';
  const successText = isDark ? '#5EE9B5' : '#166534';
  const errorBg = isDark ? '#2A1215' : '#FEF2F2';
  const errorBorder = isDark ? '#4A1C20' : '#FECACA';
  const errorText = isDark ? '#F87171' : '#991B1B';
  const modalBackdrop = isDark ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.5)';
  const modalBg = isDark ? '#1D293D' : '#FFFFFF';
  const modalInfoBg = isDark ? '#111827' : '#F9FAFB';
  const accentPurple = isDark ? '#C4B5FD' : '#7C3AED';

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Queue list state
  const [queueList, setQueueList] = useState<QueueListResponse | null>(null);
  const [selectedQueue, setSelectedQueue] = useState<string>('all');

  // Queue status state (for all queues overview)
  const [queueStatus, setQueueStatus] = useState<AllQueuesStatusResponse | null>(null);

  // Queue jobs state
  const [queueJobs, setQueueJobs] = useState<QueueJob[]>([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsPagination, setJobsPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [selectedJobStatus, setSelectedJobStatus] = useState<QueueJobStatus>('waiting');
  const [selectedJobs, setSelectedJobs] = useState<string[]>([]);

  // Action states
  const [actionLoading, setActionLoading] = useState(false);

  // Modal states
  const [showClearModal, setShowClearModal] = useState(false);
  const [showDrainModal, setShowDrainModal] = useState(false);
  const [showRetryModal, setShowRetryModal] = useState(false);
  const [showJobDetailsModal, setShowJobDetailsModal] = useState(false);
  const [selectedJobDetails, setSelectedJobDetails] = useState<QueueJob | null>(null);
  const [clearStatus, setClearStatus] = useState<string>('all');

  // Tab state
  const [activeTab, setActiveTab] = useState<'overview' | 'jobs'>('overview');

  // Fetch queue list
  const fetchQueueList = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getQueueList();
      setQueueList(response);
    } catch (err: unknown) {
      logger.error('Failed to fetch queue list:', err);
      setError(getErrorMessage(err, 'Failed to fetch queue list'));
      setQueueList(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch queue status (all or specific)
  const fetchQueueStatus = useCallback(async () => {
    try {
      const response = await getQueueStatus(selectedQueue === 'all' ? undefined : selectedQueue);
      setQueueStatus(response as AllQueuesStatusResponse);
    } catch (err: unknown) {
      logger.error('Failed to fetch queue status:', err);
      setError(getErrorMessage(err, 'Failed to fetch queue status'));
    }
  }, [selectedQueue]);

  // Fetch queue jobs
  const fetchQueueJobs = useCallback(async () => {
    try {
      setJobsLoading(true);
      const params: QueueJobsQueryParams = {
        status: selectedJobStatus,
        page: jobsPagination.page,
        limit: jobsPagination.limit
      };
      if (selectedQueue !== 'all') {
        params.queue = selectedQueue;
      }
      const response = await getQueueJobs(params);
      setQueueJobs(response.jobs || []);
      setJobsPagination(prev => ({
        ...prev,
        total: response.pagination?.total || 0,
        totalPages: response.pagination?.totalPages || 0
      }));
    } catch (err: unknown) {
      logger.error('Failed to fetch queue jobs:', err);
      setError(getErrorMessage(err, 'Failed to fetch queue jobs'));
      setQueueJobs([]);
    } finally {
      setJobsLoading(false);
    }
  }, [selectedQueue, selectedJobStatus, jobsPagination.page, jobsPagination.limit]);

  useEffect(() => {
    fetchQueueList();
  }, [fetchQueueList]);

  useEffect(() => {
    if (!loading) {
      fetchQueueStatus();
    }
  }, [loading, fetchQueueStatus]);

  useEffect(() => {
    if (activeTab === 'jobs') {
      fetchQueueJobs();
    }
  }, [activeTab, fetchQueueJobs]);

  // Handle pause/resume queue
  const handleTogglePause = async (queueName?: string) => {
    try {
      setActionLoading(true);
      const targetQueue = queueName || (selectedQueue === 'all' ? undefined : selectedQueue);

      // Check if we should pause or resume
      const shouldResume = queueName
        ? queueList?.queues?.find(q => q.name === queueName)?.isPaused
        : selectedQueue === 'all'
          ? queueStatus?.queues?.some(q => q.isPaused)
          : queueStatus?.queues?.find(q => q.name === selectedQueue)?.isPaused;

      if (shouldResume) {
        const response = await resumeQueue(targetQueue);
        setSuccessMessage(response.message || 'Queue resumed successfully');
      } else {
        const response = await pauseQueue(targetQueue);
        setSuccessMessage(response.message || 'Queue paused successfully');
      }
      fetchQueueList();
      fetchQueueStatus();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Operation failed'));
    } finally {
      setActionLoading(false);
    }
  };

  // Handle clear queue
  const handleClearQueue = async () => {
    try {
      setActionLoading(true);
      const response = await clearRedisQueue({
        queue: selectedQueue === 'all' ? undefined : selectedQueue,
        status: clearStatus as ClearQueueParams['status']
      });
      setSuccessMessage(response.message || `Cleared ${response.totalCleaned} jobs`);
      setShowClearModal(false);
      setClearStatus('all');
      fetchQueueList();
      fetchQueueStatus();
      if (activeTab === 'jobs') {
        fetchQueueJobs();
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Operation failed'));
    } finally {
      setActionLoading(false);
    }
  };

  // Handle retry failed jobs
  const handleRetryFailed = async () => {
    try {
      setActionLoading(true);
      const response = await retryFailedJobs(100, selectedQueue === 'all' ? undefined : selectedQueue);
      setSuccessMessage(response.message || `Retried ${response.totalRetried || response.retriedCount} jobs`);
      setShowRetryModal(false);
      fetchQueueList();
      fetchQueueStatus();
      if (activeTab === 'jobs') {
        fetchQueueJobs();
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Operation failed'));
    } finally {
      setActionLoading(false);
    }
  };

  // Handle drain queue
  const handleDrainQueue = async () => {
    try {
      setActionLoading(true);
      const response = await drainQueue(selectedQueue === 'all' ? undefined : selectedQueue);
      setSuccessMessage(response.message || 'Queue drained successfully');
      setShowDrainModal(false);
      fetchQueueList();
      fetchQueueStatus();
      if (activeTab === 'jobs') {
        fetchQueueJobs();
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Operation failed'));
    } finally {
      setActionLoading(false);
    }
  };

  // Handle remove single job
  const handleRemoveJob = async (jobId: string, queueName: string) => {
    try {
      setActionLoading(true);
      await removeQueueJob(jobId, queueName);
      setSuccessMessage(`Job ${jobId} removed successfully`);
      fetchQueueJobs();
      fetchQueueStatus();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Operation failed'));
    } finally {
      setActionLoading(false);
    }
  };

  // Handle bulk remove jobs
  const handleBulkRemoveJobs = async () => {
    if (selectedJobs.length === 0) return;
    try {
      setActionLoading(true);
      const response = await bulkRemoveQueueJobs(
        selectedJobs,
        selectedQueue === 'all' ? undefined : selectedQueue
      );
      setSuccessMessage(response.message || `Removed ${response.results.removed.length} jobs`);
      setSelectedJobs([]);
      fetchQueueJobs();
      fetchQueueStatus();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Operation failed'));
    } finally {
      setActionLoading(false);
    }
  };

  // Toggle job selection
  const toggleJobSelection = (jobId: string) => {
    setSelectedJobs(prev =>
      prev.includes(jobId)
        ? prev.filter(id => id !== jobId)
        : [...prev, jobId]
    );
  };

  // Select all jobs
  const toggleSelectAll = () => {
    if (selectedJobs.length === queueJobs.length) {
      setSelectedJobs([]);
    } else {
      setSelectedJobs(queueJobs.map(job => job.id));
    }
  };

  // Format number
  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  // Format timestamp
  const formatTimestamp = (timestamp: number | undefined | null) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format delay
  const formatDelay = (delay: number | undefined) => {
    if (!delay) return '-';
    const seconds = Math.floor(delay / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setJobsPagination(prev => ({ ...prev, page: newPage }));
  };

  // Get total counts for current selection
  const getTotalCounts = () => {
    if (!queueStatus) return { waiting: 0, active: 0, completed: 0, failed: 0, delayed: 0, total: 0 };
    return queueStatus.counts;
  };

  // Clear messages after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  if (loading) {
    return <Spinner size="large" text={t('superAdmin.loading', 'Loading queue info...')} fullPage />;
  }

  const counts = getTotalCounts();

  return (
    <div className="flex flex-col items-start flex-1 min-h-0 font-geist p-0 lg:pt-2 lg:pr-2 lg:pb-2 lg:pl-0" style={{ gap: '8px', background: containerBg }}>
      {/* Main Card */}
      <div className="flex flex-col items-start flex-1 min-h-0 w-full overflow-hidden rounded-none lg:rounded-2xl" style={{ background: mainBg, boxShadow: mainShadow }}>

        {/* Header */}
        <div className="flex flex-col justify-center items-start w-full flex-shrink-0 rounded-none lg:rounded-t-2xl" style={{ padding: '12px 16px', gap: '8px', borderBottom: `1px solid ${headerBorder}` }}>
          {/* Row 1: hamburger + icon + title */}
          <div className="flex flex-row items-center w-full" style={{ gap: '8px' }}>
            {/* Hamburger menu for mobile */}
            <button
              className="lg:hidden flex items-center justify-start flex-shrink-0"
              style={{ width: '24px', height: '36px', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
              onClick={toggleSidebar}
            >
              <Menu size={24} color={isDark ? '#E2E8F0' : '#314158'} />
            </button>
            {/* Icon */}
            <div className="hidden lg:flex" style={{ width: '36px', height: '36px', background: isDark ? '#1C398E' : '#EFF6FF', borderRadius: '12px', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <HardDrive size={20} style={{ color: isDark ? '#51A2FF' : '#2B7FFF' }} />
            </div>
            {/* Title */}
            <h1 style={{ fontFamily: 'Geist, sans-serif', fontSize: '20px', lineHeight: '28px', fontWeight: 700, color: titleColor, margin: 0, whiteSpace: 'nowrap' }}>
              {t('superAdmin.queueManagement.title', 'Queue Management')}
            </h1>
          </div>
          {/* Row 2: tabs + controls - scrollable */}
          <div className="w-full overflow-x-auto">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: 'fit-content' }}>
              {/* Tab Buttons (segmented control) */}
              <div style={{ display: 'flex', border: `1px solid ${headerBorder}`, borderRadius: '8px', overflow: 'hidden', height: '36px', flexShrink: 0 }}>
                <button
                  onClick={() => setActiveTab('overview')}
                  style={{
                    padding: '0 12px',
                    fontSize: '14px',
                    fontWeight: 500,
                    border: 'none',
                    cursor: 'pointer',
                    background: activeTab === 'overview' ? (isDark ? '#1C398E' : '#EFF6FF') : 'transparent',
                    color: activeTab === 'overview' ? (isDark ? '#51A2FF' : '#2B7FFF') : subtitleColor,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <Gauge size={14} />
                  Overview
                </button>
                <button
                  onClick={() => setActiveTab('jobs')}
                  style={{
                    padding: '0 12px',
                    fontSize: '14px',
                    fontWeight: 500,
                    border: 'none',
                    cursor: 'pointer',
                    background: activeTab === 'jobs' ? (isDark ? '#1C398E' : '#EFF6FF') : 'transparent',
                    color: activeTab === 'jobs' ? (isDark ? '#51A2FF' : '#2B7FFF') : subtitleColor,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <List size={14} />
                  Jobs
                </button>
              </div>

              {/* Queue Selector */}
              <select
                value={selectedQueue}
                onChange={(e) => {
                  setSelectedQueue(e.target.value);
                  setJobsPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ padding: '0 12px', height: '36px', fontSize: '13px', fontWeight: 500, borderRadius: '8px', background: inputBg, border: `1px solid ${inputBorder}`, color: inputText, flexShrink: 0 }}
              >
                <option value="all">All Queues ({queueList?.totalQueues || 0})</option>
                {queueList?.queues.map(queue => (
                  <option key={queue.name} value={queue.name}>
                    {queue.name} ({queue.total})
                  </option>
                ))}
              </select>

              {/* Action Buttons */}
              <button
                onClick={() => {
                  fetchQueueList();
                  fetchQueueStatus();
                  if (activeTab === 'jobs') fetchQueueJobs();
                }}
                style={{ height: '36px', padding: '0 12px', fontSize: '13px', fontWeight: 500, borderRadius: '8px', background: buttonBg, color: buttonText, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0, whiteSpace: 'nowrap' }}
              >
                <RefreshCcw size={14} />
                Refresh
              </button>
              <button
                onClick={() => handleTogglePause()}
                disabled={actionLoading}
                className={`${
                  queueStatus?.queues?.some(q => q.isPaused)
                    ? 'bg-green-500 text-white hover:bg-green-600'
                    : 'bg-yellow-500 text-white hover:bg-yellow-600'
                }`}
                style={{ height: '36px', padding: '0 12px', fontSize: '13px', fontWeight: 500, borderRadius: '8px', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0, whiteSpace: 'nowrap' }}
              >
                {queueStatus?.queues?.some(q => q.isPaused) ? <Play size={14} /> : <Pause size={14} />}
                {queueStatus?.queues?.some(q => q.isPaused) ? 'Resume All' : 'Pause All'}
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className={`overflow-y-auto flex-1 ${isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}`} style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>

          {/* Messages */}
          {successMessage && (
            <div className="p-3 rounded-lg text-sm flex items-center gap-2" style={{ background: successBg, border: `1px solid ${successBorder}`, color: successText }}>
              <CheckCircle2 size={16} />
              {successMessage}
            </div>
          )}
          {error && (
            <div className="p-3 rounded-lg text-sm flex items-center gap-2" style={{ background: errorBg, border: `1px solid ${errorBorder}`, color: errorText }}>
              <AlertTriangle size={16} />
              {error}
            </div>
          )}

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <>
              {/* Total Counts Card */}
              <div className="rounded-lg p-5" style={{ border: `1px solid ${headerBorder}` }}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ background: isDark ? '#2A1215' : '#FEE2E2' }}>
                      <Database size={20} style={{ color: isDark ? '#F87171' : '#DC2626' }} />
                    </div>
                    <div>
                      <h2 className="text-sm font-semibold" style={{ color: titleColor }}>
                        {selectedQueue === 'all' ? 'All Queues' : selectedQueue}
                      </h2>
                      <p className="text-xs" style={{ color: subtitleColor }}>
                        {selectedQueue === 'all'
                          ? `${queueList?.totalQueues || 0} queues total`
                          : queueList?.queues.find(q => q.name === selectedQueue)?.type === 'machine'
                            ? `Machine queue - ${queueList?.queues.find(q => q.name === selectedQueue)?.machineHost}`
                            : 'Default queue'
                        }
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold" style={{ color: accentPurple }}>{formatNumber(counts.total)}</p>
                    <p className="text-xs" style={{ color: subtitleColor }}>Total Jobs</p>
                  </div>
                </div>

                {/* Counts Grid */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="rounded-lg p-4" style={{ background: isDark ? '#2A2008' : '#FFFBEB', border: `1px solid ${isDark ? '#3D3010' : '#FEF3C7'}` }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium" style={{ color: isDark ? '#FACC15' : '#A16207' }}>Waiting</span>
                      <Hourglass size={16} style={{ color: isDark ? '#FACC15' : '#A16207' }} />
                    </div>
                    <div className="text-xl font-bold" style={{ color: isDark ? '#FACC15' : '#A16207' }}>{formatNumber(counts.waiting)}</div>
                  </div>

                  <div className="rounded-lg p-4" style={{ background: isDark ? '#0C1A2E' : '#EFF6FF', border: `1px solid ${isDark ? '#162A4A' : '#DBEAFE'}` }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium" style={{ color: isDark ? '#60A5FA' : '#1D4ED8' }}>Active</span>
                      <Loader2 size={16} style={{ color: isDark ? '#60A5FA' : '#1D4ED8' }} />
                    </div>
                    <div className="text-xl font-bold" style={{ color: isDark ? '#60A5FA' : '#1D4ED8' }}>{formatNumber(counts.active)}</div>
                  </div>

                  <div className="rounded-lg p-4" style={{ background: isDark ? '#0D2818' : '#F0FDF4', border: `1px solid ${isDark ? '#1A4028' : '#DCFCE7'}` }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium" style={{ color: isDark ? '#5EE9B5' : '#15803D' }}>Completed</span>
                      <CheckCircle2 size={16} style={{ color: isDark ? '#5EE9B5' : '#15803D' }} />
                    </div>
                    <div className="text-xl font-bold" style={{ color: isDark ? '#5EE9B5' : '#15803D' }}>{formatNumber(counts.completed)}</div>
                  </div>

                  <div className="rounded-lg p-4" style={{ background: isDark ? '#2A1215' : '#FEF2F2', border: `1px solid ${isDark ? '#4A1C20' : '#FEE2E2'}` }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium" style={{ color: isDark ? '#F87171' : '#DC2626' }}>Failed</span>
                      <XCircle size={16} style={{ color: isDark ? '#F87171' : '#DC2626' }} />
                    </div>
                    <div className="text-xl font-bold" style={{ color: isDark ? '#F87171' : '#DC2626' }}>{formatNumber(counts.failed)}</div>
                  </div>

                  <div className="rounded-lg p-4" style={{ background: isDark ? '#2A1D08' : '#FFF7ED', border: `1px solid ${isDark ? '#3D2A10' : '#FED7AA'}` }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium" style={{ color: isDark ? '#FB923C' : '#C2410C' }}>Delayed</span>
                      <Clock size={16} style={{ color: isDark ? '#FB923C' : '#C2410C' }} />
                    </div>
                    <div className="text-xl font-bold" style={{ color: isDark ? '#FB923C' : '#C2410C' }}>{formatNumber(counts.delayed)}</div>
                  </div>
                </div>
              </div>

              {/* Individual Queues List */}
              {selectedQueue === 'all' && queueList && queueList.queues.length > 0 && (
                <div>
                  <h2 className="text-sm font-semibold mb-4" style={{ color: titleColor }}>Individual Queues</h2>
                  <div className="space-y-3">
                    {queueList.queues.map((queue) => (
                      <div
                        key={queue.name}
                        className="p-4 rounded-lg"
                        style={{
                          border: '1px solid',
                          ...(queue.isPaused
                            ? { background: isDark ? '#2A2008' : '#FFFBEB', borderColor: isDark ? '#3D3010' : '#FEF3C7' }
                            : { background: isDark ? '#1D293D' : '#F9FAFB', borderColor: isDark ? '#314158' : '#E5E7EB' }
                          )
                        }}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-10 h-10 rounded-lg flex items-center justify-center"
                              style={{
                                background: queue.type === 'machine'
                                  ? (isDark ? '#0C2A2E' : '#ECFEFF')
                                  : (isDark ? '#1E1338' : '#F3E8FF')
                              }}
                            >
                              {queue.type === 'machine'
                                ? <HardDrive size={18} style={{ color: isDark ? '#22D3EE' : '#0891B2' }} />
                                : <Database size={18} style={{ color: isDark ? '#C4B5FD' : '#7C3AED' }} />
                              }
                            </div>
                            <div>
                              <h3 className="text-sm font-medium" style={{ color: titleColor }}>{queue.name}</h3>
                              <div className="flex items-center gap-2">
                                <span
                                  className="text-xs px-2 py-0.5 rounded"
                                  style={{
                                    background: queue.type === 'machine'
                                      ? (isDark ? '#0C2A2E' : '#ECFEFF')
                                      : (isDark ? '#1E1338' : '#F3E8FF'),
                                    color: queue.type === 'machine'
                                      ? (isDark ? '#22D3EE' : '#0891B2')
                                      : (isDark ? '#C4B5FD' : '#7C3AED')
                                  }}
                                >
                                  {queue.type === 'machine' ? queue.machineHost : 'Default'}
                                </span>
                                {queue.isPaused && (
                                  <span
                                    className="text-xs px-2 py-0.5 rounded flex items-center"
                                    style={{ background: isDark ? '#2A2008' : '#FEF3C7', color: isDark ? '#FACC15' : '#A16207' }}
                                  >
                                    <Pause size={12} className="mr-1" />Paused
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-lg font-bold" style={{ color: titleColor }}>{formatNumber(queue.total)}</span>
                            <button
                              onClick={() => handleTogglePause(queue.name)}
                              disabled={actionLoading}
                              className="p-2 rounded-md transition-colors"
                              style={{
                                background: queue.isPaused
                                  ? (isDark ? '#0D2818' : '#DCFCE7')
                                  : (isDark ? '#2A2008' : '#FEF3C7'),
                                color: queue.isPaused
                                  ? (isDark ? '#5EE9B5' : '#15803D')
                                  : (isDark ? '#FACC15' : '#A16207')
                              }}
                              title={queue.isPaused ? 'Resume Queue' : 'Pause Queue'}
                            >
                              {queue.isPaused ? <Play size={16} /> : <Pause size={16} />}
                            </button>
                            <button
                              onClick={() => setSelectedQueue(queue.name)}
                              className="p-2 rounded-md transition-colors"
                              style={{ background: isDark ? '#1E1338' : '#F3E8FF', color: accentPurple }}
                              title="View Queue"
                            >
                              <Eye size={16} />
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
                          <div className="text-center">
                            <div className="font-semibold" style={{ color: isDark ? '#FACC15' : '#A16207' }}>{queue.counts.waiting}</div>
                            <div style={{ color: subtitleColor }}>Waiting</div>
                          </div>
                          <div className="text-center">
                            <div className="font-semibold" style={{ color: isDark ? '#60A5FA' : '#1D4ED8' }}>{queue.counts.active}</div>
                            <div style={{ color: subtitleColor }}>Active</div>
                          </div>
                          <div className="text-center">
                            <div className="font-semibold" style={{ color: isDark ? '#5EE9B5' : '#15803D' }}>{queue.counts.completed}</div>
                            <div style={{ color: subtitleColor }}>Completed</div>
                          </div>
                          <div className="text-center">
                            <div className="font-semibold" style={{ color: isDark ? '#F87171' : '#DC2626' }}>{queue.counts.failed}</div>
                            <div style={{ color: subtitleColor }}>Failed</div>
                          </div>
                          <div className="text-center">
                            <div className="font-semibold" style={{ color: isDark ? '#FB923C' : '#C2410C' }}>{queue.counts.delayed}</div>
                            <div style={{ color: subtitleColor }}>Delayed</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              <div>
                <h2 className="text-sm font-semibold mb-4" style={{ color: titleColor }}>Quick Actions</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Clear Completed */}
                  <button
                    onClick={() => {
                      setClearStatus('completed');
                      setShowClearModal(true);
                    }}
                    disabled={actionLoading || counts.completed === 0}
                    className="p-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-left"
                    style={{ border: `1px solid ${isDark ? '#1A4028' : '#BBF7D0'}` }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: isDark ? '#0D2818' : '#DCFCE7' }}>
                        <CheckCircle2 size={18} style={{ color: isDark ? '#5EE9B5' : '#15803D' }} />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium" style={{ color: titleColor }}>Clear Completed</h3>
                        <p className="text-xs" style={{ color: subtitleColor }}>{formatNumber(counts.completed)} jobs</p>
                      </div>
                    </div>
                  </button>

                  {/* Clear Failed */}
                  <button
                    onClick={() => {
                      setClearStatus('failed');
                      setShowClearModal(true);
                    }}
                    disabled={actionLoading || counts.failed === 0}
                    className="p-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-left"
                    style={{ border: `1px solid ${isDark ? '#4A1C20' : '#FECACA'}` }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: isDark ? '#2A1215' : '#FEE2E2' }}>
                        <XCircle size={18} style={{ color: isDark ? '#F87171' : '#DC2626' }} />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium" style={{ color: titleColor }}>Clear Failed</h3>
                        <p className="text-xs" style={{ color: subtitleColor }}>{formatNumber(counts.failed)} jobs</p>
                      </div>
                    </div>
                  </button>

                  {/* Retry Failed */}
                  <button
                    onClick={() => setShowRetryModal(true)}
                    disabled={actionLoading || counts.failed === 0}
                    className="p-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-left"
                    style={{ border: `1px solid ${isDark ? '#3D2A10' : '#FED7AA'}` }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: isDark ? '#2A1D08' : '#FFF7ED' }}>
                        <RotateCcw size={18} style={{ color: isDark ? '#FB923C' : '#C2410C' }} />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium" style={{ color: titleColor }}>Retry Failed</h3>
                        <p className="text-xs" style={{ color: subtitleColor }}>Re-queue failed jobs</p>
                      </div>
                    </div>
                  </button>

                  {/* Drain Queue */}
                  <button
                    onClick={() => setShowDrainModal(true)}
                    disabled={actionLoading || (counts.waiting === 0 && counts.delayed === 0)}
                    className="p-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-left"
                    style={{ border: `1px solid ${isDark ? '#2D1B69' : '#E9D5FF'}` }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: isDark ? '#1E1338' : '#F3E8FF' }}>
                        <Filter size={18} style={{ color: accentPurple }} />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium" style={{ color: titleColor }}>Drain Queue</h3>
                        <p className="text-xs" style={{ color: subtitleColor }}>Remove waiting & delayed</p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Clear All Action */}
              <div className="flex items-center justify-between rounded-lg p-5" style={{ border: `1px solid ${headerBorder}` }}>
                <div>
                  <h2 className="text-sm font-semibold" style={{ color: titleColor }}>Clear All Jobs</h2>
                  <p className="text-xs mt-1" style={{ color: subtitleColor }}>
                    Remove all jobs from {selectedQueue === 'all' ? 'all queues' : `queue "${selectedQueue}"`} regardless of status
                  </p>
                </div>
                <button
                  onClick={() => {
                    setClearStatus('all');
                    setShowClearModal(true);
                  }}
                  disabled={actionLoading || counts.total === 0}
                  className="px-4 py-2 bg-red-500 text-white text-sm font-medium rounded-md hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Trash2 size={14} />
                  Clear All
                </button>
              </div>
            </>
          )}

          {/* Jobs Tab */}
          {activeTab === 'jobs' && (
            <>
              {/* Status Filter (no card wrapper) */}
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex flex-wrap gap-2">
                  {(['waiting', 'active', 'completed', 'failed', 'delayed'] as QueueJobStatus[]).map((status) => (
                    <button
                      key={status}
                      onClick={() => {
                        setSelectedJobStatus(status);
                        setJobsPagination(prev => ({ ...prev, page: 1 }));
                        setSelectedJobs([]);
                      }}
                      className="px-4 py-2 text-sm font-medium rounded-md transition-colors capitalize"
                      style={{
                        background: selectedJobStatus === status ? (isDark ? '#1C398E' : '#EFF6FF') : buttonBg,
                        color: selectedJobStatus === status ? (isDark ? '#51A2FF' : '#2B7FFF') : buttonText
                      }}
                    >
                      {status}
                      <span className="ml-2 text-xs">
                        ({formatNumber(counts[status])})
                      </span>
                    </button>
                  ))}
                </div>
                {selectedJobs.length > 0 && (
                  <button
                    onClick={handleBulkRemoveJobs}
                    disabled={actionLoading}
                    className="px-4 py-2 bg-red-500 text-white text-sm font-medium rounded-md hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    <Trash2 size={14} />
                    Remove Selected ({selectedJobs.length})
                  </button>
                )}
              </div>

              {/* Jobs Table (with border and overflow-hidden, no separate card wrapper) */}
              <div className="overflow-hidden" style={{ border: `1px solid ${tableBorderColor}`, borderRadius: '12px' }}>
                {jobsLoading ? (
                  <div className="p-8 text-center">
                    <Spinner size="medium" text="Loading jobs..." />
                  </div>
                ) : queueJobs.length === 0 ? (
                  <div className="p-8 text-center">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: isDark ? '#1D293D' : '#F3F4F6' }}>
                      <Inbox size={24} style={{ color: subtitleColor }} />
                    </div>
                    <h3 className="text-sm font-medium mb-1" style={{ color: titleColor }}>No {selectedJobStatus} jobs</h3>
                    <p className="text-xs" style={{ color: subtitleColor }}>There are no jobs with this status</p>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="min-w-full" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                        <thead>
                          <tr style={{ background: tableHeaderBg }}>
                            <th className="px-4 py-3 text-left" style={{ borderBottom: `1px solid ${tableBorderColor}` }}>
                              <input
                                type="checkbox"
                                checked={selectedJobs.length === queueJobs.length && queueJobs.length > 0}
                                onChange={toggleSelectAll}
                                className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                              />
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: subtitleColor, borderBottom: `1px solid ${tableBorderColor}` }}>
                              Job ID
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: subtitleColor, borderBottom: `1px solid ${tableBorderColor}` }}>
                              Name
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: subtitleColor, borderBottom: `1px solid ${tableBorderColor}` }}>
                              Queue
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: subtitleColor, borderBottom: `1px solid ${tableBorderColor}` }}>
                              Created
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: subtitleColor, borderBottom: `1px solid ${tableBorderColor}` }}>
                              {selectedJobStatus === 'delayed' ? 'Delay' : 'Processed'}
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: subtitleColor, borderBottom: `1px solid ${tableBorderColor}` }}>
                              Attempts
                            </th>
                            {selectedJobStatus === 'failed' && (
                              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: subtitleColor, borderBottom: `1px solid ${tableBorderColor}` }}>
                                Error
                              </th>
                            )}
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: subtitleColor, borderBottom: `1px solid ${tableBorderColor}` }}>
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {queueJobs.map((job) => (
                            <tr key={job.id} style={{ borderBottom: `1px solid ${tableBorderColor}` }}>
                              <td className="px-4 py-3">
                                <input
                                  type="checkbox"
                                  checked={selectedJobs.includes(job.id)}
                                  onChange={() => toggleJobSelection(job.id)}
                                  className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                                />
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className="text-sm font-mono" style={{ color: titleColor }}>{job.id}</span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className="text-sm" style={{ color: titleColor }}>{job.name || '-'}</span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className="text-xs px-2 py-1 rounded" style={{ background: isDark ? '#111827' : '#F3F4F6', color: subtitleColor }}>
                                  {job.queueName}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm" style={{ color: subtitleColor }}>
                                {formatTimestamp(job.timestamp)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm" style={{ color: subtitleColor }}>
                                {selectedJobStatus === 'delayed'
                                  ? formatDelay(job.delay)
                                  : formatTimestamp(job.processedOn)
                                }
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className="text-sm font-medium" style={{ color: job.attemptsMade > 1 ? (isDark ? '#FB923C' : '#C2410C') : subtitleColor }}>
                                  {job.attemptsMade}
                                </span>
                              </td>
                              {selectedJobStatus === 'failed' && (
                                <td className="px-4 py-3 max-w-xs">
                                  {job.failedReason ? (
                                    <span className="text-xs truncate block" style={{ color: isDark ? '#F87171' : '#DC2626' }} title={job.failedReason}>
                                      {job.failedReason.length > 50 ? job.failedReason.substring(0, 50) + '...' : job.failedReason}
                                    </span>
                                  ) : (
                                    <span className="text-xs" style={{ color: subtitleColor }}>-</span>
                                  )}
                                </td>
                              )}
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => {
                                      setSelectedJobDetails(job);
                                      setShowJobDetailsModal(true);
                                    }}
                                    className="p-1.5 rounded transition-colors"
                                    style={{ color: accentPurple }}
                                    title="View Details"
                                  >
                                    <Eye size={16} />
                                  </button>
                                  <button
                                    onClick={() => handleRemoveJob(job.id, job.queueName)}
                                    disabled={actionLoading}
                                    className="p-1.5 rounded transition-colors disabled:opacity-50"
                                    style={{ color: isDark ? '#F87171' : '#DC2626' }}
                                    title="Remove Job"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    <div className="px-4 py-3 flex items-center justify-between flex-wrap gap-2" style={{ background: tableHeaderBg, borderTop: `1px solid ${tableBorderColor}` }}>
                      <div className="text-sm" style={{ color: paginationText }}>
                        Showing <span className="font-medium">{(jobsPagination.page - 1) * jobsPagination.limit + 1}</span> to{' '}
                        <span className="font-medium">
                          {Math.min(jobsPagination.page * jobsPagination.limit, jobsPagination.total)}
                        </span>{' '}
                        of <span className="font-medium">{formatNumber(jobsPagination.total)}</span> jobs
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handlePageChange(jobsPagination.page - 1)}
                          disabled={jobsPagination.page === 1}
                          className="px-3 py-1.5 text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ color: paginationText, background: paginationBg, border: `1px solid ${paginationBorder}` }}
                        >
                          Previous
                        </button>
                        <span className="text-sm" style={{ color: paginationText }}>
                          Page {jobsPagination.page} of {jobsPagination.totalPages}
                        </span>
                        <button
                          onClick={() => handlePageChange(jobsPagination.page + 1)}
                          disabled={jobsPagination.page >= jobsPagination.totalPages}
                          className="px-3 py-1.5 text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ color: paginationText, background: paginationBg, border: `1px solid ${paginationBorder}` }}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Clear Queue Modal */}
      {showClearModal && ReactDOM.createPortal(
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: modalBackdrop, backdropFilter: 'blur(4px)' }}>
          <div className="rounded-lg p-6 max-w-md w-full mx-4" style={{ background: modalBg }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: isDark ? '#2A1215' : '#FEE2E2' }}>
                <AlertTriangle size={20} style={{ color: isDark ? '#F87171' : '#DC2626' }} />
              </div>
              <h3 className="text-lg font-semibold" style={{ color: titleColor }}>
                Clear {clearStatus === 'all' ? 'All' : clearStatus.charAt(0).toUpperCase() + clearStatus.slice(1)} Jobs
              </h3>
            </div>
            <p className="text-sm mb-4" style={{ color: subtitleColor }}>
              Are you sure you want to clear {clearStatus === 'all' ? 'all' : clearStatus} jobs from
              {selectedQueue === 'all' ? ' all queues' : ` queue "${selectedQueue}"`}?
              This action cannot be undone.
            </p>
            <div className="rounded-lg p-3 mb-4" style={{ background: modalInfoBg }}>
              <div className="flex justify-between text-sm">
                <span style={{ color: subtitleColor }}>Jobs to remove:</span>
                <span className="font-medium" style={{ color: titleColor }}>
                  {clearStatus === 'all'
                    ? formatNumber(counts.total)
                    : formatNumber(counts[clearStatus as keyof typeof counts] || 0)}
                </span>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowClearModal(false);
                  setClearStatus('all');
                }}
                className="px-4 py-2 text-sm font-medium rounded-md transition-colors"
                style={{ background: buttonBg, color: buttonText }}
              >
                Cancel
              </button>
              <button
                onClick={handleClearQueue}
                disabled={actionLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-md hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {actionLoading && <Loader2 size={14} className="animate-spin" />}
                Clear Jobs
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Retry Failed Modal */}
      {showRetryModal && ReactDOM.createPortal(
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: modalBackdrop, backdropFilter: 'blur(4px)' }}>
          <div className="rounded-lg p-6 max-w-md w-full mx-4" style={{ background: modalBg }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: isDark ? '#2A1D08' : '#FFF7ED' }}>
                <RotateCcw size={20} style={{ color: isDark ? '#FB923C' : '#C2410C' }} />
              </div>
              <h3 className="text-lg font-semibold" style={{ color: titleColor }}>Retry Failed Jobs</h3>
            </div>
            <p className="text-sm mb-4" style={{ color: subtitleColor }}>
              This will re-queue all failed jobs from
              {selectedQueue === 'all' ? ' all queues' : ` queue "${selectedQueue}"`} for processing.
              Jobs will be retried up to their maximum attempt limit.
            </p>
            <div className="rounded-lg p-3 mb-4" style={{ background: modalInfoBg }}>
              <div className="flex justify-between text-sm">
                <span style={{ color: subtitleColor }}>Failed jobs to retry:</span>
                <span className="font-medium" style={{ color: isDark ? '#FB923C' : '#C2410C' }}>{formatNumber(counts.failed)}</span>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowRetryModal(false)}
                className="px-4 py-2 text-sm font-medium rounded-md transition-colors"
                style={{ background: buttonBg, color: buttonText }}
              >
                Cancel
              </button>
              <button
                onClick={handleRetryFailed}
                disabled={actionLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-md hover:bg-orange-600 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {actionLoading && <Loader2 size={14} className="animate-spin" />}
                Retry Jobs
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Drain Queue Modal */}
      {showDrainModal && ReactDOM.createPortal(
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: modalBackdrop, backdropFilter: 'blur(4px)' }}>
          <div className="rounded-lg p-6 max-w-md w-full mx-4" style={{ background: modalBg }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: isDark ? '#1E1338' : '#F3E8FF' }}>
                <Filter size={20} style={{ color: accentPurple }} />
              </div>
              <h3 className="text-lg font-semibold" style={{ color: titleColor }}>Drain Queue</h3>
            </div>
            <p className="text-sm mb-4" style={{ color: subtitleColor }}>
              This will remove all waiting and delayed jobs from
              {selectedQueue === 'all' ? ' all queues' : ` queue "${selectedQueue}"`}.
              Active jobs will continue processing.
            </p>
            <div className="rounded-lg p-3 mb-4 space-y-2" style={{ background: modalInfoBg }}>
              <div className="flex justify-between text-sm">
                <span style={{ color: subtitleColor }}>Waiting jobs to remove:</span>
                <span className="font-medium" style={{ color: isDark ? '#FACC15' : '#A16207' }}>{formatNumber(counts.waiting)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span style={{ color: subtitleColor }}>Delayed jobs to remove:</span>
                <span className="font-medium" style={{ color: isDark ? '#FB923C' : '#C2410C' }}>{formatNumber(counts.delayed)}</span>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDrainModal(false)}
                className="px-4 py-2 text-sm font-medium rounded-md transition-colors"
                style={{ background: buttonBg, color: buttonText }}
              >
                Cancel
              </button>
              <button
                onClick={handleDrainQueue}
                disabled={actionLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-500 rounded-md hover:bg-purple-600 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {actionLoading && <Loader2 size={14} className="animate-spin" />}
                Drain Queue
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Job Details Modal */}
      {showJobDetailsModal && selectedJobDetails && ReactDOM.createPortal(
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: modalBackdrop, backdropFilter: 'blur(4px)' }}>
          <div className="rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" style={{ background: modalBg }}>
            {/* Modal Header */}
            <div className="px-6 py-4 flex items-center justify-between" style={{ background: isDark ? 'linear-gradient(to right, #1E1338, #1D293D)' : 'linear-gradient(to right, #FAF5FF, #FFFFFF)', borderBottom: `1px solid ${tableBorderColor}` }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: isDark ? '#1E1338' : '#F3E8FF' }}>
                  <FileText size={18} style={{ color: accentPurple }} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold" style={{ color: titleColor }}>Job Details</h3>
                  <p className="text-xs" style={{ color: subtitleColor }}>ID: {selectedJobDetails.id}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowJobDetailsModal(false);
                  setSelectedJobDetails(null);
                }}
                className="w-8 h-8 flex items-center justify-center rounded-full transition-colors"
                style={{ color: subtitleColor }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Body */}
            <div className={`flex-1 overflow-y-auto p-6 space-y-4 ${isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-lg p-4" style={{ background: modalInfoBg }}>
                  <label className="text-xs font-medium uppercase tracking-wider" style={{ color: subtitleColor }}>Name</label>
                  <p className="mt-1 text-sm font-medium" style={{ color: titleColor }}>{selectedJobDetails.name || '-'}</p>
                </div>
                <div className="rounded-lg p-4" style={{ background: modalInfoBg }}>
                  <label className="text-xs font-medium uppercase tracking-wider" style={{ color: subtitleColor }}>Queue</label>
                  <p className="mt-1 text-sm font-medium" style={{ color: titleColor }}>{selectedJobDetails.queueName}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-lg p-4" style={{ background: modalInfoBg }}>
                  <label className="text-xs font-medium uppercase tracking-wider" style={{ color: subtitleColor }}>Created</label>
                  <p className="mt-1 text-sm font-medium" style={{ color: titleColor }}>{formatTimestamp(selectedJobDetails.timestamp)}</p>
                </div>
                <div className="rounded-lg p-4" style={{ background: modalInfoBg }}>
                  <label className="text-xs font-medium uppercase tracking-wider" style={{ color: subtitleColor }}>Processed</label>
                  <p className="mt-1 text-sm font-medium" style={{ color: titleColor }}>{formatTimestamp(selectedJobDetails.processedOn)}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="rounded-lg p-4" style={{ background: modalInfoBg }}>
                  <label className="text-xs font-medium uppercase tracking-wider" style={{ color: subtitleColor }}>Attempts</label>
                  <p className="mt-1 text-sm font-medium" style={{ color: titleColor }}>{selectedJobDetails.attemptsMade}</p>
                </div>
                <div className="rounded-lg p-4" style={{ background: modalInfoBg }}>
                  <label className="text-xs font-medium uppercase tracking-wider" style={{ color: subtitleColor }}>Priority</label>
                  <p className="mt-1 text-sm font-medium" style={{ color: titleColor }}>{selectedJobDetails.priority || '-'}</p>
                </div>
                <div className="rounded-lg p-4" style={{ background: modalInfoBg }}>
                  <label className="text-xs font-medium uppercase tracking-wider" style={{ color: subtitleColor }}>Delay</label>
                  <p className="mt-1 text-sm font-medium" style={{ color: titleColor }}>{formatDelay(selectedJobDetails.delay)}</p>
                </div>
              </div>

              {selectedJobDetails.failedReason && (
                <div className="rounded-lg p-4" style={{ background: isDark ? '#2A1215' : '#FEF2F2', border: `1px solid ${isDark ? '#4A1C20' : '#FEE2E2'}` }}>
                  <label className="text-xs font-medium uppercase tracking-wider mb-2 flex items-center" style={{ color: isDark ? '#F87171' : '#DC2626' }}>
                    <AlertTriangle size={12} className="mr-1" /> Failed Reason
                  </label>
                  <p className="text-sm whitespace-pre-wrap break-words" style={{ color: isDark ? '#F87171' : '#991B1B' }}>{selectedJobDetails.failedReason}</p>
                </div>
              )}

              <div className="rounded-lg p-4" style={{ background: modalInfoBg }}>
                <label className="text-xs font-medium uppercase tracking-wider mb-2 block" style={{ color: subtitleColor }}>Job Data</label>
                <pre className="text-xs overflow-x-auto p-3 rounded" style={{ background: isDark ? '#0B1120' : '#FFFFFF', color: isDark ? '#90A1B9' : '#374151', border: `1px solid ${isDark ? '#1E293B' : '#E5E7EB'}` }}>
                  {JSON.stringify(selectedJobDetails.data, null, 2)}
                </pre>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 flex justify-between" style={{ background: isDark ? '#111827' : '#F9FAFB', borderTop: `1px solid ${tableBorderColor}` }}>
              <button
                onClick={() => handleRemoveJob(selectedJobDetails.id, selectedJobDetails.queueName)}
                disabled={actionLoading}
                className="px-4 py-2 text-sm font-medium rounded-md transition-colors disabled:opacity-50 flex items-center gap-2"
                style={{ color: isDark ? '#F87171' : '#DC2626', background: isDark ? '#2A1215' : '#FEF2F2', border: `1px solid ${isDark ? '#4A1C20' : '#FECACA'}` }}
              >
                <Trash2 size={14} />
                Remove Job
              </button>
              <button
                onClick={() => {
                  setShowJobDetailsModal(false);
                  setSelectedJobDetails(null);
                }}
                className="px-4 py-2 text-sm font-medium rounded-md transition-colors"
                style={{ color: buttonText, background: paginationBg, border: `1px solid ${paginationBorder}` }}
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
